import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { adminService } from '../../services/adminService';
import './AdminOrders.css';

const AdminOrders = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    if (orderId) {
      fetchOrderById(orderId);
    } else {
      fetchOrders();
    }
  }, [orderId, statusFilter, page]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminService.getOrders(
        page,
        20,
        statusFilter === 'ALL' ? null : statusFilter
      );
      if (response.success) {
        // Handle both Page and List response formats
        const ordersData = response.data?.content || response.data;
        setOrders(Array.isArray(ordersData) ? ordersData : []);
        setTotalPages(response.data?.totalPages || 1);
      } else {
        setError(response.message || 'Failed to load orders');
      }
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrderById = async (id) => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminService.getOrderById(id);
      if (response.success) {
        setSelectedOrder(response.data);
      } else {
        setError(response.message || 'Order not found');
      }
    } catch (err) {
      console.error('Error fetching order:', err);
      setError('Failed to load order details');
    } finally {
      setLoading(false);
    }
  };

  const statusOptions = [
    { value: 'ALL', label: 'All Orders' },
    { value: 'PENDING', label: 'Pending' },
    { value: 'CONFIRMED', label: 'Confirmed' },
    { value: 'PROCESSING', label: 'Processing' },
    { value: 'SHIPPED', label: 'Shipped' },
    { value: 'DELIVERED', label: 'Delivered' },
    { value: 'CANCELLED', label: 'Cancelled' },
    { value: 'REFUNDED', label: 'Refunded' },
  ];

  if (loading) {
    return (
      <div className="admin-orders">
        <div className="admin-loading">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-orders">
        <div className="admin-error">
          <p>{error}</p>
          <button
            onClick={() => (orderId ? fetchOrderById(orderId) : fetchOrders())}
            className="retry-btn"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Show order detail view if orderId is present
  if (orderId && selectedOrder) {
    return (
      <OrderDetail
        order={selectedOrder}
        onBack={() => navigate('/admin/orders')}
        onStatusUpdate={fetchOrderById}
      />
    );
  }

  return (
    <div className="admin-orders">
      <div className="orders-header">
        <div className="orders-filters">
          {statusOptions.map((option) => (
            <button
              key={option.value}
              className={`filter-btn ${statusFilter === option.value ? 'active' : ''}`}
              onClick={() => {
                setStatusFilter(option.value);
                setPage(0);
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
        <div className="orders-count">Showing {orders.length} orders</div>
      </div>

      <div className="orders-table-container">
        <table className="orders-table">
          <thead>
            <tr>
              <th>Order ID</th>
              <th>Customer</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 ? (
              <tr>
                <td colSpan="6" className="no-data">
                  No orders found
                </td>
              </tr>
            ) : (
              orders.map((order) => (
                <motion.tr
                  key={order.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="order-row"
                >
                  <td className="order-id">{order.orderNumber}</td>
                  <td>
                    <div className="customer-info">
                      <div className="customer-name">
                        {order.customerName || 'N/A'}
                      </div>
                      <div className="customer-email">
                        {order.customerEmail || ''}
                      </div>
                    </div>
                  </td>
                  <td className="amount">₹{order.total?.toLocaleString()}</td>
                  <td>
                    <span
                      className={`status-badge status-${order.status?.toLowerCase()}`}
                    >
                      {order.status}
                    </span>
                  </td>
                  <td>
                    {order.createdAt
                      ? new Date(order.createdAt).toLocaleString()
                      : 'N/A'}
                  </td>
                  <td>
                    <button
                      className="view-btn"
                      onClick={() => navigate(`/admin/orders/${order.id}`)}
                    >
                      View Details
                    </button>
                  </td>
                </motion.tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            Previous
          </button>
          <span>
            Page {page + 1} of {totalPages}
          </span>
          <button
            disabled={page >= totalPages - 1}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

const OrderDetail = ({ order, onBack, onStatusUpdate }) => {
  const [status, setStatus] = useState(order.status);
  const [trackingNumber, setTrackingNumber] = useState(
    order.trackingNumber || ''
  );
  const [notes, setNotes] = useState(order.notes || '');
  const [updating, setUpdating] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [successModal, setSuccessModal] = useState({ show: false, type: '', message: '', amount: 0 });
  const [showShippingModal, setShowShippingModal] = useState(false);
  const [shippingDetails, setShippingDetails] = useState({
    trackingNumber: order.trackingNumber || '',
    deliveryPartner: order.deliveryPartner || 'Delhivery',
    trackingUrl: order.trackingUrl || ''
  });

  const handleStatusUpdate = async (newStatus) => {
    // Show confirmation for cancellation
    if (newStatus === 'CANCELLED') {
      setShowCancelConfirm(true);
      return;
    }

    // Show shipping modal for SHIPPED status
    if (newStatus === 'SHIPPED') {
      setShowShippingModal(true);
      return;
    }

    await executeStatusUpdate(newStatus);
  };

  const executeStatusUpdate = async (newStatus, reason = null) => {
    try {
      setUpdating(true);
      const response = await adminService.updateOrderStatus(order.id, {
        status: newStatus,
        trackingNumber: trackingNumber || null,
        notes: reason || notes || null,
      });
      if (response.success) {
        setStatus(newStatus);
        onStatusUpdate(order.id);
        if (newStatus === 'CANCELLED') {
          setSuccessModal({
            show: true,
            type: 'refund',
            message: 'Order has been cancelled and refund initiated',
            amount: order.total
          });
        }
      } else {
        setSuccessModal({
          show: true,
          type: 'error',
          message: response.message || 'Failed to update status',
          amount: 0
        });
      }
    } catch (err) {
      console.error('Error updating status:', err);
      setSuccessModal({
        show: true,
        type: 'error',
        message: err.response?.data?.message || 'Failed to update status',
        amount: 0
      });
    } finally {
      setUpdating(false);
      setShowCancelConfirm(false);
      setCancelReason('');
    }
  };

  const handleCancelConfirm = () => {
    if (!cancelReason.trim()) {
      setSuccessModal({
        show: true,
        type: 'error',
        message: 'Please provide a reason for cancellation',
        amount: 0
      });
      return;
    }
    executeStatusUpdate('CANCELLED', cancelReason);
  };

  const handleShippingConfirm = async () => {
    if (!shippingDetails.trackingNumber.trim()) {
      setSuccessModal({
        show: true,
        type: 'error',
        message: 'Please enter a tracking number',
        amount: 0
      });
      return;
    }

    try {
      setUpdating(true);
      const response = await adminService.updateOrderStatus(order.id, {
        status: 'SHIPPED',
        trackingNumber: shippingDetails.trackingNumber,
        deliveryPartner: shippingDetails.deliveryPartner,
        trackingUrl: shippingDetails.trackingUrl || `https://www.delhivery.com/track/package/${shippingDetails.trackingNumber}`,
        notes: notes || null,
      });
      if (response.success) {
        setStatus('SHIPPED');
        setTrackingNumber(shippingDetails.trackingNumber);
        onStatusUpdate(order.id);
        setSuccessModal({
          show: true,
          type: 'success',
          message: 'Order marked as shipped! Customer will receive an email with tracking details.',
          amount: 0
        });
      } else {
        setSuccessModal({
          show: true,
          type: 'error',
          message: response.message || 'Failed to update status',
          amount: 0
        });
      }
    } catch (err) {
      console.error('Error updating status:', err);
      setSuccessModal({
        show: true,
        type: 'error',
        message: err.response?.data?.message || 'Failed to mark as shipped',
        amount: 0
      });
    } finally {
      setUpdating(false);
      setShowShippingModal(false);
    }
  };

  const handleSaveTracking = async () => {
    try {
      setUpdating(true);
      const response = await adminService.updateOrderStatus(order.id, {
        status: status,
        trackingNumber: trackingNumber,
        notes: notes,
      });
      if (response.success) {
        setSuccessModal({
          show: true,
          type: 'success',
          message: 'Tracking information saved successfully',
          amount: 0
        });
        onStatusUpdate(order.id);
      } else {
        setSuccessModal({
          show: true,
          type: 'error',
          message: response.message || 'Failed to save tracking',
          amount: 0
        });
      }
    } catch (err) {
      console.error('Error saving tracking:', err);
      setSuccessModal({
        show: true,
        type: 'error',
        message: 'Failed to save tracking information',
        amount: 0
      });
    } finally {
      setUpdating(false);
    }
  };

  const statusFlow = {
    PENDING: ['CONFIRMED', 'CANCELLED'],
    CONFIRMED: ['PROCESSING', 'CANCELLED'],
    PROCESSING: ['SHIPPED', 'CANCELLED'],
    SHIPPED: ['OUT_FOR_DELIVERY', 'DELIVERED'],
    OUT_FOR_DELIVERY: ['DELIVERED'],
    DELIVERED: ['RETURNED'],
    RETURNED: ['REFUNDED'],
    CANCELLED: ['REFUNDED'],
    REFUNDED: [],
  };

  const availableActions = statusFlow[status] || [];

  // Get address display
  const address = order.shippingAddress;
  const fullAddress = address
    ? `${address.addressLine1}${address.addressLine2 ? ', ' + address.addressLine2 : ''}, ${address.city}, ${address.state} - ${address.pincode}`
    : 'No address';

  return (
    <div className="order-detail">
      {/* Success/Error Modal */}
      {successModal.show && (
        <div className="modal-overlay">
          <div className={`modal-content result-modal ${successModal.type}`}>
            <div className="result-modal-icon">
              {successModal.type === 'refund' && (
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
                </svg>
              )}
              {successModal.type === 'success' && (
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
              )}
              {successModal.type === 'error' && (
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="15" y1="9" x2="9" y2="15"/>
                  <line x1="9" y1="9" x2="15" y2="15"/>
                </svg>
              )}
            </div>
            <h3 className="result-modal-title">
              {successModal.type === 'refund' && 'Refund Initiated'}
              {successModal.type === 'success' && 'Success'}
              {successModal.type === 'error' && 'Error'}
            </h3>
            {successModal.type === 'refund' && (
              <div className="result-modal-amount">₹{successModal.amount?.toLocaleString()}</div>
            )}
            <p className="result-modal-message">{successModal.message}</p>
            {successModal.type === 'refund' && (
              <div className="result-modal-info">
                <div className="info-item">
                  <span className="info-icon">⏱</span>
                  <span>Processing time: 5-7 business days</span>
                </div>
                <div className="info-item">
                  <span className="info-icon">💳</span>
                  <span>Refund to original payment method</span>
                </div>
              </div>
            )}
            <button
              className="result-modal-btn"
              onClick={() => setSuccessModal({ show: false, type: '', message: '', amount: 0 })}
            >
              {successModal.type === 'error' ? 'Try Again' : 'Done'}
            </button>
          </div>
        </div>
      )}

      {/* Cancel Confirmation Modal */}
      {showCancelConfirm && (
        <div className="modal-overlay">
          <div className="modal-content cancel-modal">
            <h3>Cancel Order & Initiate Refund</h3>
            <p className="modal-warning">
              This action will cancel the order and automatically initiate a refund of <strong>₹{order.total?.toLocaleString()}</strong> to the customer's original payment method.
            </p>
            <div className="modal-field">
              <label>Reason for cancellation *</label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Enter reason for cancellation..."
                rows="3"
              />
            </div>
            <div className="modal-actions">
              <button
                className="modal-btn cancel"
                onClick={() => {
                  setShowCancelConfirm(false);
                  setCancelReason('');
                }}
                disabled={updating}
              >
                Go Back
              </button>
              <button
                className="modal-btn confirm-cancel"
                onClick={handleCancelConfirm}
                disabled={updating || !cancelReason.trim()}
              >
                {updating ? 'Processing...' : 'Cancel Order & Refund'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Shipping Details Modal */}
      {showShippingModal && (
        <div className="modal-overlay">
          <div className="modal-content shipping-modal">
            <h3>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '8px', verticalAlign: 'middle' }}>
                <rect x="1" y="3" width="15" height="13" rx="2" ry="2"/>
                <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/>
                <circle cx="5.5" cy="18.5" r="2.5"/>
                <circle cx="18.5" cy="18.5" r="2.5"/>
              </svg>
              Shipping Details
            </h3>
            <p className="modal-info">
              Enter the tracking details. An email with this information will be sent to the customer.
            </p>

            <div className="modal-field">
              <label>Delivery Partner</label>
              <select
                value={shippingDetails.deliveryPartner}
                onChange={(e) => setShippingDetails({
                  ...shippingDetails,
                  deliveryPartner: e.target.value
                })}
              >
                <option value="Delhivery">Delhivery</option>
                <option value="DTDC">DTDC</option>
                <option value="BlueDart">Blue Dart</option>
                <option value="Ecom Express">Ecom Express</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div className="modal-field">
              <label>Tracking Number / AWB *</label>
              <input
                type="text"
                value={shippingDetails.trackingNumber}
                onChange={(e) => setShippingDetails({
                  ...shippingDetails,
                  trackingNumber: e.target.value
                })}
                placeholder="Enter tracking number or AWB"
              />
            </div>

            <div className="modal-field">
              <label>Tracking URL *</label>
              <input
                type="text"
                value={shippingDetails.trackingUrl}
                onChange={(e) => setShippingDetails({
                  ...shippingDetails,
                  trackingUrl: e.target.value
                })}
                placeholder="Paste the tracking URL here"
              />
              <small className="field-hint">This link will be included in the email sent to the customer</small>
            </div>

            <div className="shipping-preview">
              <div className="preview-title">Customer Email Preview</div>
              <div className="preview-content">
                <p>Your order <strong>{order.orderNumber}</strong> has been shipped!</p>
                <p>
                  <strong>Delivery Partner:</strong> {shippingDetails.deliveryPartner}<br/>
                  <strong>Tracking Number:</strong> {shippingDetails.trackingNumber || '—'}<br/>
                  {shippingDetails.trackingUrl && (
                    <>
                      <strong>Track your order:</strong>{' '}
                      <span className="preview-link">{shippingDetails.trackingUrl}</span>
                    </>
                  )}
                </p>
              </div>
            </div>

            <div className="modal-actions">
              <button
                className="modal-btn cancel"
                onClick={() => {
                  setShowShippingModal(false);
                  setShippingDetails({
                    trackingNumber: order.trackingNumber || '',
                    deliveryPartner: order.deliveryPartner || 'Delhivery',
                    trackingUrl: order.trackingUrl || ''
                  });
                }}
                disabled={updating}
              >
                Cancel
              </button>
              <button
                className="modal-btn confirm-ship"
                onClick={handleShippingConfirm}
                disabled={updating || !shippingDetails.trackingNumber.trim()}
              >
                {updating ? (
                  <>Processing...</>
                ) : (
                  <>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '6px' }}>
                      <path d="M22 2L11 13"/>
                      <path d="M22 2L15 22L11 13L2 9L22 2Z"/>
                    </svg>
                    Mark as Shipped & Notify
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <button className="back-btn" onClick={onBack} disabled={updating}>
        ← Back to Orders
      </button>

      <div className="order-detail-header">
        <div>
          <h2 className="order-detail-title">Order {order.orderNumber}</h2>
          <div className="order-meta">
            <span>
              Placed on{' '}
              {order.createdAt
                ? new Date(order.createdAt).toLocaleString()
                : 'N/A'}
            </span>
            <span className={`status-badge status-${status?.toLowerCase()}`}>
              {status}
            </span>
          </div>
        </div>
        <div className="order-actions">
          {availableActions.map((action) => (
            <button
              key={action}
              className={`action-btn action-${action.toLowerCase()}`}
              onClick={() => handleStatusUpdate(action)}
              disabled={updating}
            >
              {action === 'CONFIRMED' && 'Confirm Order'}
              {action === 'PROCESSING' && 'Start Processing'}
              {action === 'SHIPPED' && 'Mark as Shipped'}
              {action === 'OUT_FOR_DELIVERY' && 'Out for Delivery'}
              {action === 'DELIVERED' && 'Mark as Delivered'}
              {action === 'RETURNED' && 'Mark as Returned'}
              {action === 'REFUNDED' && 'Mark as Refunded'}
              {action === 'CANCELLED' && 'Cancel Order'}
            </button>
          ))}
        </div>
      </div>

      <div className="order-detail-content">
        <div className="order-section">
          <h3 className="section-title">Customer Information</h3>
          <div className="info-grid">
            <div>
              <label>Name</label>
              <div>{order.customerName || 'N/A'}</div>
            </div>
            <div>
              <label>Email</label>
              <div>{order.customerEmail || 'N/A'}</div>
            </div>
            <div>
              <label>Shipping Address</label>
              <div>{fullAddress}</div>
            </div>
          </div>
        </div>

        {/* Refund Status Section */}
        {(status === 'CANCELLED' || status === 'REFUNDED') && (
          <div className="order-section refund-section">
            <h3 className="section-title">Refund Information</h3>
            <div className="refund-status-box">
              {status === 'CANCELLED' && (
                <>
                  <div className="refund-status pending">
                    <span className="status-icon">⏳</span>
                    <div>
                      <strong>Refund Initiated</strong>
                      <p>Refund of ₹{order.total?.toLocaleString()} is being processed. It will be credited to the customer's original payment method within 5-7 business days.</p>
                    </div>
                  </div>
                </>
              )}
              {status === 'REFUNDED' && (
                <>
                  <div className="refund-status completed">
                    <span className="status-icon">✓</span>
                    <div>
                      <strong>Refund Completed</strong>
                      <p>₹{order.total?.toLocaleString()} has been refunded to the customer's original payment method.</p>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {(status === 'SHIPPED' ||
          status === 'OUT_FOR_DELIVERY' ||
          status === 'DELIVERED') && (
          <div className="order-section">
            <h3 className="section-title">Tracking Information</h3>
            <div className="tracking-input">
              <input
                type="text"
                placeholder="Enter tracking number"
                value={trackingNumber}
                onChange={(e) => setTrackingNumber(e.target.value)}
                disabled={updating}
              />
              <button
                className="save-btn"
                onClick={handleSaveTracking}
                disabled={updating}
              >
                {updating ? 'Saving...' : 'Save Tracking'}
              </button>
            </div>
            {trackingNumber && (
              <div className="tracking-display">
                Tracking: <strong>{trackingNumber}</strong>
              </div>
            )}
          </div>
        )}

        <div className="order-section">
          <h3 className="section-title">Order Notes</h3>
          <textarea
            className="notes-input"
            placeholder="Add notes about this order..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows="3"
            disabled={updating}
          />
        </div>

        <div className="order-section">
          <h3 className="section-title">Order Items</h3>
          <table className="items-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>SKU</th>
                <th>Quantity</th>
                <th>Price</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {order.items && order.items.length > 0 ? (
                order.items.map((item, index) => (
                  <tr key={item.id || index}>
                    <td>{item.productVariantName || 'N/A'}</td>
                    <td>{item.productVariantSku || 'N/A'}</td>
                    <td>{item.quantity}</td>
                    <td>₹{item.priceAtPurchase?.toLocaleString()}</td>
                    <td>₹{item.subtotal?.toLocaleString()}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="no-data">
                    No items
                  </td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan="4" className="total-label">
                  Subtotal
                </td>
                <td className="total-amount">
                  ₹{order.subtotal?.toLocaleString()}
                </td>
              </tr>
              <tr>
                <td colSpan="4" className="total-label">
                  Shipping
                </td>
                <td className="total-amount">
                  ₹{order.shippingCost?.toLocaleString() || '0'}
                </td>
              </tr>
              <tr>
                <td colSpan="4" className="total-label">
                  <strong>Total</strong>
                </td>
                <td className="total-amount">
                  <strong>₹{order.total?.toLocaleString()}</strong>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminOrders;
